<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  formatArea,
  formatBRL,
  numericValue,
  pluralizeFeature,
} from '../utils/reportFormatters';

// Card "Relatório de Desempenho": resumo para o proprietário, capa do imóvel,
// faixa de preço sugerida e quebra de valores. Toda a cópia derivada mora aqui.
const props = defineProps({
  selectedProperty: { type: Object, default: () => ({}) },
  selectedPropertyLabel: { type: String, default: '' },
  priceAnalysis: { type: Object, default: () => ({}) },
  ownerShare: { type: Object, default: () => ({}) },
  performanceCopy: { type: Object, default: () => ({}) },
  isRental: { type: Boolean, default: false },
});

const propertyDescriptors = computed(() =>
  [
    formatArea(props.selectedProperty.area),
    pluralizeFeature(
      props.selectedProperty.bedrooms,
      'quarto',
      'quartos',
      'sem quartos'
    ),
    pluralizeFeature(
      props.selectedProperty.parking_spaces,
      'vaga',
      'vagas',
      'sem vagas'
    ),
    pluralizeFeature(
      props.selectedProperty.bathrooms,
      'banheiro',
      'banheiros',
      'sem banheiros'
    ),
  ].filter(Boolean)
);

const reportPropertyLocation = computed(() =>
  [props.selectedProperty.bairro, props.selectedProperty.cidade]
    .filter(Boolean)
    .join(' · ')
);
const reportPropertyAddress = computed(
  () => props.selectedProperty.address || reportPropertyLocation.value
);
const ownerSummaryTitle = computed(
  () => props.ownerShare.summary_title || 'Olá, proprietário.'
);
const ownerSummaryMessage = computed(
  () =>
    props.ownerShare.summary_message ||
    'Nós da Imudei, realizamos uma análise do seu imóvel e encontramos o preço ideal.'
);
const ownerPropertyAlertTitle = computed(
  () => props.ownerShare.property_alert_title || 'Seu imóvel:'
);
const ownerPropertyAlertMessage = computed(() => {
  if (props.ownerShare.property_alert_message) {
    return props.ownerShare.property_alert_message;
  }

  return reportPropertyAddress.value
    ? `Imóvel localizado na ${reportPropertyAddress.value}.`
    : '';
});
const reportCoverTitle = computed(
  () => props.ownerShare.cover_title || 'Análise de preço do seu imóvel'
);
const reportCoverText = computed(
  () =>
    props.ownerShare.cover_text ||
    'Em parceria com a sua imobiliária, preparamos uma análise completa para te ajudar com o preço ideal do seu imóvel'
);

const priceRecommendationText = computed(() => {
  if (props.priceAnalysis.status === 'unavailable') {
    return 'Ainda não existem dados suficientes para serem mostrados. Em breve essas informações estarão aqui.';
  }

  if (props.priceAnalysis.status === 'needs_discount') {
    return `Recomendação para baixar ${props.priceAnalysis.recommendation_percentage}% do valor atual`;
  }

  return 'Preços dentro da faixa recomendada geram mais visitas';
});

const priceAnalysisText = computed(() => {
  if (props.priceAnalysis.status === 'needs_discount') {
    return 'De acordo com a análise de desempenho, seu imóvel está precificado acima do valor de mercado, o que tem refletido em poucas visualizações e visitas.';
  }

  if (props.priceAnalysis.status === 'good_price') {
    return 'Preços dentro da faixa recomendada geram mais visitas';
  }

  return 'Sem informações para exibir';
});

const priceHeadlineLabel = computed(() =>
  props.priceAnalysis.current_value &&
  props.priceAnalysis.suggested_value &&
  Number(props.priceAnalysis.current_value) ===
    Number(props.priceAnalysis.suggested_value)
    ? 'Valor definido'
    : props.performanceCopy.suggestion_price_label || 'Valor sugerido'
);
const priceBoxTitle = computed(
  () =>
    props.performanceCopy.price_box_title ||
    'Melhore o desempenho do seu imóvel com preço competitivo'
);
const priceBoxText = computed(
  () =>
    props.performanceCopy.price_box_text ||
    'Já negociamos muitos imóveis parecidos no mesmo bairro.'
);
const limitedRegionData = computed(
  () =>
    props.priceAnalysis.status === 'unavailable' ||
    Number(props.priceAnalysis.samples || 0) < 3
);
const currentPriceAboveMax = computed(() => {
  const current = Number(props.priceAnalysis.current_value || 0);
  const max = Number(props.priceAnalysis.max_suggested_value || 0);
  return current > 0 && max > 0 && current > max;
});
const currentPriceWithinRange = computed(() => {
  const current = Number(props.priceAnalysis.current_value || 0);
  const min = Number(props.priceAnalysis.min_suggested_value || 0);
  const max = Number(props.priceAnalysis.max_suggested_value || 0);
  return current > 0 && min > 0 && max > 0 && current >= min && current <= max;
});
const priceQualityTitle = computed(() => {
  if (currentPriceAboveMax.value) {
    return 'O valor está acima do valor máximo sugerido';
  }
  if (currentPriceWithinRange.value) return 'Excelente';
  if (limitedRegionData.value) return 'Dados limitados na região';
  return 'Valor sugerido';
});
const priceQualityText = computed(() => {
  if (currentPriceAboveMax.value) {
    return `Imóveis como este costumam atrair menos interessados quando têm preço acima de ${formatBRL(props.priceAnalysis.max_suggested_value)}`;
  }
  if (currentPriceWithinRange.value) {
    return 'Preços dentro da faixa recomendada geram mais visitas';
  }
  if (limitedRegionData.value) {
    return 'Temos poucos imóveis negociados na região, o que afeta o grau de confiança sobre os valores sugeridos.';
  }
  return priceAnalysisText.value;
});

const adminFeePercentage = computed(
  () => numericValue(props.selectedProperty.admin_fee_percentage) ?? 10
);
const minimumAdminFee = computed(() =>
  numericValue(props.selectedProperty.minimum_admin_fee)
);
const reportRentValue = computed(
  () =>
    numericValue(props.selectedProperty.valor_aluguel) ??
    numericValue(props.priceAnalysis.current_value)
);
const reportSaleValue = computed(
  () =>
    numericValue(props.selectedProperty.valor_venda) ??
    numericValue(props.priceAnalysis.current_value)
);
const reportAdminFeeValue = computed(() => {
  const rent = reportRentValue.value;
  if (rent == null) return null;

  const percentageValue = (rent * adminFeePercentage.value) / 100;
  return Math.max(percentageValue, minimumAdminFee.value || 0);
});
const ownerReceiveAmount = computed(() => {
  if (reportRentValue.value == null) return null;
  return Math.max(reportRentValue.value - (reportAdminFeeValue.value || 0), 0);
});
const priceValueRows = computed(() => {
  if (props.isRental) {
    return [
      {
        label: 'Aluguel',
        value: reportRentValue.value,
      },
      {
        label: 'Valor total anual do IPTU',
        value: props.selectedProperty.valor_iptu,
      },
      {
        label: 'Valor do condomínio',
        value: props.selectedProperty.valor_condominio,
      },
      {
        label: `Taxa de adm. (${adminFeePercentage.value}%)`,
        value: reportAdminFeeValue.value,
      },
      {
        label: 'Taxa mínima de adm.',
        value: minimumAdminFee.value,
      },
      {
        label: 'Proprietário vai receber',
        value: ownerReceiveAmount.value,
        highlight: true,
      },
    ];
  }

  return [
    {
      label: 'Valor do anúncio',
      value: reportSaleValue.value,
    },
    {
      label: 'Valor total anual do IPTU',
      value: props.selectedProperty.valor_iptu,
    },
    {
      label: 'Valor do condomínio',
      value: props.selectedProperty.valor_condominio,
    },
  ];
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard>
    <template #header>
      <div class="flex min-w-0 flex-col gap-1">
        <h2 class="font-redHatDisplay text-[18px] font-semibold text-lais-text">
          Relatório de Desempenho
        </h2>
        <p class="text-[13px] leading-relaxed text-lais-text-body">
          A Imudei preparou uma análise de resultado dos últimos 30 dias do seu
          imóvel.
        </p>
        <p
          v-if="selectedPropertyLabel"
          class="text-[12px] font-semibold text-lais-primary"
        >
          {{ selectedPropertyLabel }}
        </p>
      </div>
    </template>

    <div v-if="selectedProperty.id" class="space-y-6">
      <div
        class="rounded-lais border border-lais-primary/20 bg-lais-primary-50/40 p-4"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start">
          <span
            class="i-lucide-house mx-auto mt-1 size-8 shrink-0 text-lais-primary lg:mx-0"
            aria-hidden="true"
          />
          <div class="min-w-0 flex-1">
            <p
              class="font-redHatDisplay text-[18px] font-semibold text-lais-text"
            >
              {{ ownerSummaryTitle }}
            </p>
            <p class="mt-2 text-[13px] leading-relaxed text-lais-text-body">
              {{ ownerSummaryMessage }}
            </p>
            <div
              v-if="ownerPropertyAlertMessage"
              class="mt-4 rounded-lais border border-lais-border bg-white p-3"
            >
              <p class="text-[13px] font-semibold text-lais-text">
                {{ ownerPropertyAlertTitle }}
              </p>
              <p class="mt-1 text-[13px] font-semibold text-lais-text-body">
                {{ ownerPropertyAlertMessage }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="min-w-0">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ reportCoverTitle }}
          </p>
          <h3
            class="mt-1 truncate font-redHatDisplay text-[20px] font-semibold text-lais-text"
          >
            {{ selectedProperty.title }}
          </h3>
          <p class="mt-2 max-w-[680px] text-[13px] text-lais-text-body">
            {{ reportCoverText }}
          </p>
          <p
            v-if="reportPropertyAddress"
            class="mt-1 text-[13px] text-lais-text-body"
          >
            {{ reportPropertyAddress }}
          </p>
          <div class="mt-3 flex flex-wrap gap-2">
            <span
              v-if="selectedProperty.code"
              class="rounded-full border border-lais-primary/30 bg-lais-primary-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
            >
              Código {{ selectedProperty.code }}
            </span>
            <span
              v-for="descriptor in propertyDescriptors"
              :key="descriptor"
              class="rounded-full border border-lais-border bg-white px-3 py-1 text-[12px] font-semibold text-lais-text"
            >
              {{ descriptor }}
            </span>
          </div>
        </div>
        <div
          class="rounded-lais border border-lais-border bg-white px-4 py-3 text-left lg:min-w-[210px]"
        >
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ priceHeadlineLabel }}
          </p>
          <p
            class="mt-1 font-redHatDisplay text-[28px] font-semibold text-lais-primary"
          >
            {{ formatBRL(priceAnalysis.suggested_value) }}
          </p>
          <p class="text-[12px] text-lais-text-muted">
            {{ formatBRL(priceAnalysis.price_per_area) }}/m²
          </p>
        </div>
      </div>

      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Valor atual
          </p>
          <p class="mt-2 text-[18px] font-semibold text-lais-text">
            {{ formatBRL(priceAnalysis.current_value) }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Mais rápido
          </p>
          <p class="mt-2 text-[18px] font-semibold text-lais-text">
            {{ formatBRL(priceAnalysis.min_suggested_value) }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Valor máximo
          </p>
          <p class="mt-2 text-[18px] font-semibold text-lais-text">
            {{ formatBRL(priceAnalysis.max_suggested_value) }}
          </p>
        </div>
        <div
          class="rounded-lais border p-4"
          :class="
            currentPriceAboveMax
              ? 'border-lais-danger/40 bg-lais-danger/5'
              : 'border-lais-primary bg-lais-primary-50'
          "
        >
          <p
            class="text-[11px] font-semibold uppercase tracking-wide"
            :class="
              currentPriceAboveMax ? 'text-lais-danger' : 'text-lais-primary'
            "
          >
            {{ priceQualityTitle }}
          </p>
          <p
            class="mt-2 text-[14px] font-semibold leading-relaxed text-lais-text"
          >
            {{ priceQualityText }}
          </p>
        </div>
      </div>

      <div class="rounded-lais border border-lais-border bg-white p-4">
        <div
          class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
        >
          <div class="min-w-0">
            <h3
              class="font-redHatDisplay text-[16px] font-semibold text-lais-text"
            >
              {{ priceBoxTitle }}
            </h3>
            <p
              class="mt-2 max-w-[720px] text-[13px] leading-relaxed text-lais-text-body"
            >
              {{ priceQualityText }}
            </p>
            <p
              v-if="!limitedRegionData"
              class="mt-2 text-[13px] leading-relaxed text-lais-text-body"
            >
              {{ priceBoxText }}
            </p>
            <p class="mt-2 text-[13px] font-semibold text-lais-primary">
              {{ priceRecommendationText }}
            </p>
          </div>
          <p
            class="rounded-full bg-lais-bg-soft px-3 py-1 text-[12px] font-semibold text-lais-text-body"
          >
            {{ priceAnalysis.samples }} imóveis similares
          </p>
        </div>
      </div>

      <div class="rounded-lais border border-lais-border bg-white p-4">
        <div class="flex flex-col gap-1">
          <h3
            class="font-redHatDisplay text-[16px] font-semibold text-lais-text"
          >
            Valores
          </h3>
          <p class="text-[13px] leading-relaxed text-lais-text-body">
            Veja os valores usados na análise antes de enviar a avaliação para o
            proprietário.
          </p>
        </div>
        <div class="mt-4 divide-y divide-lais-border-soft">
          <div
            v-for="row in priceValueRows"
            :key="row.label"
            class="flex items-center justify-between gap-4 py-3"
            :class="row.highlight ? 'text-lais-primary' : 'text-lais-text'"
          >
            <span class="text-[13px] font-semibold">
              {{ row.label }}
            </span>
            <strong class="text-[14px] tabular-nums">
              {{ formatBRL(row.value) }}
            </strong>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="py-14 text-center">
      <span
        class="i-lucide-file-warning mx-auto block size-10 text-lais-text-empty"
        aria-hidden="true"
      />
      <p class="mt-3 text-[14px] font-semibold text-lais-text">
        Sem informações para exibir
      </p>
      <p
        class="mx-auto mt-1 max-w-[420px] text-[13px] leading-relaxed text-lais-text-muted"
      >
        Ainda não existem dados suficientes para serem mostrados. Em breve essas
        informações estarão aqui.
      </p>
    </div>
  </LaisCard>
</template>
